import type { ReactNode } from "react";
import { ErrorBoundary } from "@sentry/react";
import { AlertTriangle, RefreshCw, Home } from "lucide-react";
import { Button } from "./ui/button";

function FullPageError({ error, resetError, eventId }: { error: Error; resetError: () => void; eventId?: string }) {
  return (
    <div className="min-h-dvh bg-slate-50 px-4 py-10 flex items-center justify-center">
      <div className="w-full max-w-lg rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex items-start gap-4">
          <div className="flex h-11 w-11 items-center justify-center rounded-full bg-red-50 text-red-600">
            <AlertTriangle className="h-5 w-5" />
          </div>
          <div className="space-y-2">
            <h1 className="text-xl font-semibold text-slate-900">Something went wrong</h1>
            <p className="text-sm text-slate-600">
              The app hit an unexpected error. You can try reloading the page or go back to the dashboard.
            </p>
          </div>
        </div>

        <div className="mt-5 rounded-xl bg-slate-50 p-4 text-sm text-slate-700">
          <p className="font-medium text-slate-900">Reference</p>
          <p className="mt-1 break-all text-slate-600">{eventId ? eventId : "No Sentry event ID was attached."}</p>
        </div>

        {import.meta.env.DEV ? (
          <pre className="mt-4 max-h-48 overflow-auto rounded-xl bg-slate-950 p-4 text-xs text-slate-100 whitespace-pre-wrap">
            {error.stack || error.message}
          </pre>
        ) : null}

        <div className="mt-6 flex flex-col gap-3 sm:flex-row">
          <Button onClick={resetError} className="sm:flex-1">
            <RefreshCw className="mr-2 h-4 w-4" />
            Try again
          </Button>
          <Button variant="outline" onClick={() => (window.location.href = "/")} className="sm:flex-1">
            <Home className="mr-2 h-4 w-4" />
            Go home
          </Button>
        </div>
      </div>
    </div>
  );
}

export function AppErrorBoundary({ children }: { children: ReactNode }) {
  return (
    <ErrorBoundary fallback={({ error, resetError, eventId }) => <FullPageError error={error} resetError={resetError} eventId={eventId} />}>
      {children}
    </ErrorBoundary>
  );
}
